import styles from './specialDiscountItemForm.module.css'
import { useEffect, useMemo, useState } from "react"
import { Form, ModalId, useUIActions, FormField, FieldType, O, FieldValue, Icon, onManualChange, PriceField, PercentField, StaticValue } from "../../../../../features/ui"
import { TableName } from "../../../../../tables"
import { Product, ProductSelectField, useProductLoaders } from "../../../../products"
import { PricesList, SpecialDiscountItem } from "../../../state/types"
import { usePricesHooks } from "../../../../operations"
import { round } from "../../../../../utils/typeUtils"

type Props = {
    defaultData?: SpecialDiscountItem
    pricesList: PricesList
    filterProducts?: (product: Product) => boolean
    onSubmit: (data: SpecialDiscountItem) => void
}

export const SpecialDiscountItemForm = (props: Props) => {
    const { defaultData, pricesList, filterProducts, onSubmit } = props
    const createMode = !defaultData

    const pricesHooks = usePricesHooks()
    const productLoaders = useProductLoaders()

    const [products, setProducts] = useState<Product[]>([])
    const [productId, setProductId] = useState<O<string>>(defaultData?.productId)
    const [specialDiscountYield, setSpecialDiscountYield] = useState<O<number>>(defaultData?.specialDiscountYield)
    const [finalPrice, setFinalPrice] = useState<O<number>>(defaultData?.finalPrice)

    const { closeModal } = useUIActions()

    const listPrice = useMemo(() => productId ? pricesList[productId] : undefined, [productId, pricesList])

    useEffect(() => {
        const { loadAllProducts } = productLoaders()
        loadAllProducts().then(loadedProducts => {
            const activeProducts = loadedProducts.filter(p => p.id === defaultData?.productId || !p.archived)
            setProducts(filterProducts ? activeProducts.filter(filterProducts) : activeProducts)
        })
    }, [])

    const onChangeProduct = (value?: string) => {
        const newProductId = value as O<string>
        setProductId(newProductId)
        const newListPrice = newProductId ? pricesList[newProductId] : undefined
        if (newListPrice) {
            const defaultYield = 5
            setSpecialDiscountYield(defaultYield)
            setFinalPrice(pricesHooks.adjustment.apply(newListPrice, -defaultYield))
        } else {
            setSpecialDiscountYield(undefined)
            setFinalPrice(undefined)
        }
    }

    const onChangeSpecialYield = onManualChange((value?: FieldValue) => {
        const newYield = value as O<number>
        setSpecialDiscountYield(newYield)
        if (newYield && listPrice) {
            setFinalPrice(pricesHooks.adjustment.apply(listPrice, -newYield))
        }
    })

    const onChangeSpecialPrice = onManualChange((value?: FieldValue) => {
        const newPrice = value as O<number>
        setFinalPrice(newPrice)
        if (newPrice && listPrice) {
            setSpecialDiscountYield(-pricesHooks.adjustment.calculateYield(newPrice, listPrice))
        }
    })

    const hasValidPrice = !!productId && !!listPrice
    const isValid = hasValidPrice && !!specialDiscountYield && !!finalPrice

    const fields: FormField[] = [
        {
            name: 'productId',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            label: 'Producto',
            loading: products.length === 0,
            render: () => <ProductSelectField
                productList={products}
                showStock={false}
                defaultValue={productId}
                onChange={onChangeProduct}
            />
        },
        {
            name: 'listPrice',
            type: FieldType.PRICE,
            label: 'Precio de Lista',
            render: () => <StaticValue value={listPrice} type={FieldType.PRICE} />
        }
    ]

    if (hasValidPrice) {
        fields.push({
            name: 'discount',
            type: FieldType.PRICE,
            label: 'Descuento Especial',
            render: () => (
                <div className={styles.specialDiscount}>
                    <PercentField
                        id="specialYield"
                        value={specialDiscountYield ? round(specialDiscountYield) : undefined}
                        onChange={onChangeSpecialYield}
                    />
                    <Icon icon="arrow-left-right" small />
                    <PriceField
                        id="finalPrice"
                        value={finalPrice}
                        min={1}
                        onChange={onChangeSpecialPrice}
                    />
                </div>
            )
        })
    }

    const handleSubmit = () => {
        if (!isValid) return
        onSubmit({
            productId: productId!,
            listPrice: listPrice!,
            specialDiscountYield: specialDiscountYield!,
            finalPrice: finalPrice!
        })
        closeModal(ModalId.SPECIAL_DISCOUNT_ITEM_FORM)
    }

    const handleCancel = () => closeModal(ModalId.SPECIAL_DISCOUNT_ITEM_FORM)

    return (
        <Form
            formId="special-discount-item-form"
            fields={fields}
            createMode={createMode}
            createLabel="Agregar"
            disableSubmit={!isValid}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
        />
    )
}
